import React, { useEffect, useState } from "react";
import pbkdf2 from "pbkdf2";
import sss from "shamirs-secret-sharing";
import { Grid, Typography } from "@mui/material";
import TextInput from "components/TextInput";
import Button from "components/NewButton";
import { useLocation, useNavigate } from "react-router-dom";
import AuthHeader from "components/AuthHeader";
import { useAppDispatch, useAppSelector } from "store/store";
import {
  createEVMAccount,
  createEVMSmartAccount,
  decryptMessage,
  encryptMessage,
  getEnsFromAddress,
  showAlert,
  updateEncryptedKeys,
} from "utils/utils";
import {
  setHashedPassword,
  setHashedPin,
  setRecoveryCode,
  setTorusKey,
} from "@slices/walletSlice";
import { initalizeBlockNumbers } from "utils/blocknumber";
import {
  setAccounts,
  setActiveAccount,
  setBlockNumbers,
  setEncryptedSeedPhraseWithPassword,
  setExpirationTime,
  setHoldings,
  setMode,
  setRootAccountInfo,
} from "@slices/appSlice";
import { Accounts } from "interfaces";
import {
  LOGIN_EXPIRY,
  SHARES,
  THRESHOLD,
  stepsCount,
} from "../../../../../../src/constants";
import { Box } from "@mui/system";
import CustomizedSteppers from "components/Stepper";
import StepperForPages from "components/Stepper/StepperForPages";
import BlackTick from "assets/black-tick.svg";
import CancelIcon from "assets/cancel.svg";

const RecoverCreatePassword = () => {
  const [securePassword, setSecurePassword] = useState(true);
  const [secureConfirmPassword, setSecureConfirmPassword] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isNumber, setIsNumber] = useState(false);
  const [isUppercase, setIsUppercase] = useState(false);
  const [isLowercase, setIsLowercase] = useState(false);
  const [isSpecialCharacter, setIsSpecialCharacter] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const location = useLocation();

  const { state } = location || {};
  const { currentStep, pathName } = state || {};

  const [step, setStep] = useState(parseInt(currentStep) || 0);

  const {
    activeNetwork,
    user: { email },
    accessToken,
    fetchedKeys: { key1, key2, key3, key4, rootAddress },
  } = useAppSelector((state) => state.app);

  const { hashedPin, torusKey, recoveryCode } = useAppSelector(
    (state) => state.wallet
  );

  useEffect(() => {
    if (!email) {
      navigate("/recover");
    }
  }, []);

  const fromStringToBuffer = (val) => Buffer.from(val, "hex");
  const bufferToReadable = (buffer) => {
    return buffer.toString("hex");
  };

  const fetchAndSaveData = async (
    address: string,
    privateKey: string,
    newAccount: Accounts,
    counterfactual: string
  ) => {
    dispatch(setExpirationTime(new Date().getTime() + LOGIN_EXPIRY));

    dispatch(setAccounts(newAccount));
    dispatch(
      setActiveAccount({
        address,
        secret: privateKey,
        smartAccountAddress: counterfactual,
        accountName: newAccount[address]?.name,
      })
    );
    setStep(4);
    navigate("/account-recovered", {
      state: { currentStep: step + 1, pathName: pathName, type: "Password" },
    });
  };

  const handlePasswordContinue = async () => {
    try {
      const regex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[-_=#$@!%&*?])[A-Za-z\d-_=#$@!%&*?]{8,30}$/;
      setLoading(true);
      if (password !== confirmPassword) {
        setError(
          "The passwords you entered do not match. Please check and try again"
        );

        setLoading(false);
      } else {
        if (password.length < 8) {
          setError("Passwords must be 8 characters long");

          setLoading(false);
        } else if (!regex.test(password)) {
          setError(
            "Password must include numbers, lowercase letters, uppercase letters and special characters"
          );

          setLoading(false);
        } else {
          const hashedPassword = pbkdf2
            .pbkdf2Sync(password, "salt", 1, 32, "sha512")
            .toString("hex");

          dispatch(setHashedPassword(hashedPassword));

          const decryptedKey1 = decryptMessage(key1, torusKey);
          const decryptedKey2 = decryptMessage(key4, recoveryCode);
          const decryptedKey3 = decryptMessage(key3, hashedPin);

          console.log(decryptedKey1, decryptedKey2, decryptedKey3);
          const decryptMnemonic = sss
            .combine([
              fromStringToBuffer(decryptedKey1),
              fromStringToBuffer(decryptedKey2),
              fromStringToBuffer(decryptedKey3),
            ])
            .toString();

          console.log(decryptMnemonic);

          const secretBuffer = Buffer.from(decryptMnemonic);
          const generatedShares = sss.split(secretBuffer, {
            shares: SHARES,
            threshold: THRESHOLD,
          });

          let s = generatedShares.map((s) => bufferToReadable(s));

          const encryptedShare1 = encryptMessage(s[0], torusKey);
          const encryptedShare2 = encryptMessage(s[1], hashedPassword);
          const encryptedShare3 = encryptMessage(s[2], hashedPin);

          const updatedRecoveryCode = torusKey + hashedPassword + hashedPin;

          const encryptedShare4 = encryptMessage(s[3], updatedRecoveryCode);

          await updateEncryptedKeys(
            email,
            encryptedShare1,
            encryptedShare2,
            encryptedShare3,
            encryptedShare4,
            accessToken
          );

          const encryptedSeedPhraseWithPassword = encryptMessage(
            decryptMnemonic,
            hashedPassword
          );

          const ens = await getEnsFromAddress(rootAddress?.toLowerCase());

          const blockNumbers = await initalizeBlockNumbers();

          dispatch(setBlockNumbers(blockNumbers));

          const { newAccount, address, secret, counterfactual } =
            await createEVMSmartAccount(
              decryptMnemonic,
              1,
              "",
              hashedPassword,
              activeNetwork
            );

          const { address: derivedRootAddress, secret: rootSecret } =
            await createEVMAccount(decryptMnemonic, 0, ens, hashedPassword);

          dispatch(
            setEncryptedSeedPhraseWithPassword(encryptedSeedPhraseWithPassword)
          );

          dispatch(setMode("google"));
          if (rootAddress.toLowerCase() === derivedRootAddress.toLowerCase()) {
            dispatch(
              setRootAccountInfo({
                address: rootAddress,
                name: ens,
                secret: rootSecret,
              })
            );
          }

          await fetchAndSaveData(address, secret, newAccount, counterfactual);

          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const handleKeyPress = (e: any) => {
    const { key, keyCode } = e || {};
    if (key === "Enter" || keyCode === 13 || key === "Tab" || keyCode === 9) {
      e.preventDefault(); // Prevent the default behavior of the Tab key
      handlePasswordContinue();
    }
  };

  const onPasswordChange = (e) => {
    const newPassword = e;
    const hasNumber = /[0-9]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
    const isLengthValid = newPassword.length >= 8;

    setPassword(e);

    if (hasNumber) setIsNumber(true);
    else setIsNumber(false);
    if (hasLowerCase) setIsLowercase(true);
    else setIsLowercase(false);
    if (hasUpperCase) setIsUppercase(true);
    else setIsUppercase(false);
    if (hasSpecialCharacter) setIsSpecialCharacter(true);
    else setIsSpecialCharacter(false);
    // else {
    //   setIsNumber(false);
    //   setIsLowercase(false);
    //   setIsUppercase(false);
    //   setIsSpecialCharacter(false);
    // }
  };

  const totalSteps = stepsCount[pathName] || 3;

  return (
    <div className="create-account-container">
      <Grid container spacing={2}>
        <Grid item xs={8} className="auth-header-with-steps">
          <AuthHeader />
        </Grid>
        <Grid
          item
          xs={16}
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Box
            className="auth-header-steps"
            width={"50%"}
            style={{
              position: "absolute",
              top: "54px",
            }}
          >
            <StepperForPages
              step={step}
              steps={new Array(totalSteps).fill("")}
              changeStep={(selectedStep: number) => {
                //eg. if user is on step 3 he should be able to move at step 1 or 2 on clicking step icon
                if (selectedStep < step) {
                  setStep(selectedStep);
                  navigate(-1);
                }
              }}
            />
          </Box>
        </Grid>
      </Grid>
      <div className="create-account-box password-box">
        <Typography
          variant="h2"
          sx={{
            margin: "30px 0px 30px 0px",

            fontWeight: "700",
          }}
        >
          Create your password
        </Typography>

        <div className="welcome-box-container">
          <TextInput
            title="Enter Password"
            value={password}
            onChange={onPasswordChange}
            onKeyPress={(e) => handleKeyPress(e)}
            showSecure={() => setSecurePassword(!securePassword)}
            showEndIcon
            secure={securePassword}
            type={securePassword ? "password" : "text"}
            style={{
              width: "370px",
            }}
          />
          <div className="password-validator-box">
            <div className="password-validator">
              {isNumber ? (
                <img src={BlackTick} alt="" />
              ) : (
                <img src={CancelIcon} alt="" height={20} />
              )}
              <Typography variant="body2" className="password-validator-text">
                Password must include numbers
              </Typography>
            </div>

            <div className="password-validator">
              {isLowercase && isUppercase ? (
                <img src={BlackTick} alt="" />
              ) : (
                <img src={CancelIcon} alt="" height={20} />
              )}
              <Typography variant="body2" className="password-validator-text">
                Password must include lower and uppercase letters
              </Typography>
            </div>

            <div className="password-validator">
              {isSpecialCharacter ? (
                <img src={BlackTick} alt="" />
              ) : (
                <img src={CancelIcon} alt="" height={20} />
              )}
              <Typography variant="body2" className="password-validator-text">
                Password must include special characters
              </Typography>
            </div>

            <div className="password-validator">
              {password.length > 8 ? (
                <img src={BlackTick} alt="" />
              ) : (
                <img src={CancelIcon} alt="" height={20} />
              )}
              <Typography variant="body2" className="password-validator-text">
                Password must be 8 characters long
              </Typography>
            </div>
          </div>
          <TextInput
            title="Confirm Password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            showSecure={() => setSecureConfirmPassword(!secureConfirmPassword)}
            showEndIcon
            secure={secureConfirmPassword}
            type={secureConfirmPassword ? "password" : "text"}
            onKeyPress={handleKeyPress}
            style={{
              width: "370px",
            }}
          />

          {error && (
            <div className="password-validator-box">
              <Typography
                style={{ color: "red", fontSize: 14, marginTop: -20 }}
              >
                {error}
              </Typography>
            </div>
          )}

          <Button
            onClick={handlePasswordContinue}
            title="Continue"
            loading={loading}
            disabled={
              !password ||
              !confirmPassword ||
              !isNumber ||
              !isLowercase ||
              !isUppercase ||
              !isSpecialCharacter
            }
            style={{
              width: "390px",
              padding: "15px 0px",
              margin: "20px 0px 0px 0px",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default RecoverCreatePassword;
